import type { Produit } from "../../../shared/src/app/concerts";
import { activation, checkout, type ErreurListeAttente, listeAttente, visite } from "./api";

/**
 * Le pont entre la page et les mesures (ticket 4, #316). La page (home.tsx, fausse-porte.tsx)
 * n'appelle que ces fonctions et n'envoie rien elle-meme ; elles deleguent a `api.ts`, qui ne
 * leve jamais.
 *
 * - `onChargement()` : une fois au chargement de la page, `GET /api/visite` avec les `utm_*`
 *   de l'adresse. Un drapeau de module empeche le second appel du double effet de React
 *   StrictMode (et tout remontage de la page).
 * - `onPrixClique(product)` : a chaque clic sur un prix, avant l'ouverture de la fenetre de la
 *   fausse porte ; pose un `checkout`. La page n'attend pas.
 * - `onActivation(count)` : appelee a chaque changement du nombre de groupes, tant qu'il vaut
 *   dix ou plus. La page ne retient rien : `api.activation` ne compte l'activation qu'une fois
 *   par navigateur (indicateur `concerts.activation-envoyee` pose seulement apres une reponse
 *   2xx, repli en memoire si le stockage est refuse, un seul envoi a la fois). `count` est le
 *   seul chiffre de la liste qui quitte le navigateur. La page n'attend pas.
 * - `onListeAttente({ email, product, consent, piege })` : a l'envoi du formulaire, apres le
 *   controle local. Rend `{ ok: true }`, ou `{ ok: false, erreur }` avec une phrase francaise
 *   prete a afficher (traduction des codes d'`api.ts` dans `MESSAGES_LISTE_ATTENTE`).
 */

export type Product = Produit;

export interface WaitlistInput {
  email: string;
  product: Product;
  consent: boolean;
  piege: string;
}

export interface WaitlistResult {
  ok: boolean;
  erreur?: string;
}

/** Ce que la fenetre dit pour chaque refus de la liste d'attente. */
export const MESSAGES_LISTE_ATTENTE: Record<ErreurListeAttente, string> = {
  consentement: "Coche la case pour qu'on puisse t'écrire.",
  email: "Cette adresse ne semble pas valide.",
  invalide: "La demande n'a pas pu être lue. Vérifie ton adresse et réessaie.",
  "trop-de-requetes": "Trop de demandes en ce moment, réessaie dans une minute.",
  indisponible: "L'inscription ne répond pas pour l'instant. Réessaie plus tard.",
  reseau: "La connexion a échoué. Vérifie ton réseau et réessaie.",
};

let visiteEnvoyee = false;

export function onChargement(): void {
  if (visiteEnvoyee) {
    return;
  }
  visiteEnvoyee = true;
  visite().catch(() => undefined);
}

export function onPrixClique(product: Product): void {
  checkout(product).catch(() => undefined);
}

export function onActivation(count: number): void {
  activation(count).catch(() => undefined);
}

export async function onListeAttente(input: WaitlistInput): Promise<WaitlistResult> {
  try {
    const result = await listeAttente(input);
    if (result.ok) {
      return { ok: true };
    }
    return {
      ok: false,
      erreur: MESSAGES_LISTE_ATTENTE[result.erreur ?? "indisponible"],
    };
  } catch {
    return { ok: false, erreur: MESSAGES_LISTE_ATTENTE.indisponible };
  }
}

/** Pour les tests : oublie que la visite est partie. */
export function oublierVisitePourLesTests(): void {
  visiteEnvoyee = false;
}
